import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLayoutEffect } from 'react';
import { notification } from 'antd';
import { getErrorMessage } from 'helpers/errorHandler';
import { chatDebug } from 'helpers/chatDebugLog';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import React from 'react';

function AxiosWrapper({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	useLayoutEffect(() => {
		const requestInterceptor = axios.interceptors.request.use(
			(config) => {
				config.headers['x-TraceId'] = uuidv4();
				config.headers['Portal'] = 'trader';
				// Auth lives in HttpOnly cookies, which cross-origin requests only send with credentials.
				config.withCredentials = true;
				return config;
			},
			(error) => {
				return Promise.reject(error);
			},
		);

		const responseInterceptor = axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;
				const errorCode = Number(error?.response?.data.code);
				const data = error?.response?.data?.extraAttributes ?? '';
				const errorMessage = getErrorMessage(errorCode, t, data);

				// No response (timeout, network down): nothing meaningful to show, and callers handle it.
				if (errorMessage && error.response && error.response.status !== 401 && errorCode !== 2043) {
					notification['error']({ message: '', description: errorMessage, placement: 'bottom' });
				}
				if (error.response && error.response.status === 401 && originalRequest.url?.includes('login')) {
					notification['error']({ message: '', description: errorMessage, placement: 'bottom' });
				}
				if (error.response && error.response.status === 401 && !originalRequest.url?.includes('login')) {
					chatDebug.warn('auth', '401, redirecting to /redirect', { url: originalRequest.url });
					navigate('/redirect');
				}

				return Promise.reject(error);
			},
		);
		return () => {
			axios.interceptors.request.eject(requestInterceptor);
			axios.interceptors.response.eject(responseInterceptor);
		};
	}, [t]);
	return <>{children}</>;
}

export const MemoizedAxiosWrapper = React.memo(AxiosWrapper);
